import { Button } from '@/components/shadcn/button';
import SettingsTabTitle from '../../settings-tab-title';
import { PlusIcon, Cross1Icon } from '@radix-ui/react-icons';
import { Dispatch, SetStateAction, useState } from 'react';
import GooglePlacesAutocomplete from '../../../google-places-autocomplete/google-places-autocomplete';
import { useOrganizationContext } from '@/contexts/organization-context';
import { gql } from '@/__generated__';
import { useMutation } from '@apollo/client';
import { useToast } from '@/components/shadcn/use-toast';
import ServedLocationItem from './served-location-item';
import { OrganizationGeoLocationV2 } from '@/__generated__/graphql';
import { logError } from '@/lib/rum';

type ServedLocationsProps = {
  setLoading: Dispatch<SetStateAction<boolean>>;
  locations: Pick<OrganizationGeoLocationV2, 'id' | 'locationName'>[];
};

const AddOrganizationGeoLocationMutation =
  gql(`mutation AddOrganizationGeoLocationMutation($input: AddOrganizationGeoLocationInput!) {
    grantSettings {
      addOrganizationGeoLocation(input: $input) {
        result {
          id
          locationName
        }
      }
    }
  }`);

export function ServedLocations(props: ServedLocationsProps) {
  const { toast } = useToast();
  const { organizationId } = useOrganizationContext();

  const [isAddNewLocation, setIsAddNewLocation] = useState(false);

  const [addOrgGeoLocation] = useMutation(AddOrganizationGeoLocationMutation, {
    onCompleted: () => {
      toast({
        description: 'Location added successfully',
        duration: 3000,
      });

      setIsAddNewLocation(false);
      props.setLoading(false);
    },
    onError(error) {
      logError('Failed to add organization geo-location', error);
      toast({
        description: 'Failed to add location',
        duration: 3000,
        variant: 'destructive',
      });
      props.setLoading(false);
    },
    refetchQueries: ['FetchGrantSettings'],
  });

  const onPlaceSelect = (placeData: any) => {
    // create organization geo-location
    const orgGeoLocationInput = {
      organizationId: organizationId as string,
      locationName: placeData.formatted_address,
      bounds: {
        northeast: placeData.geometry.viewport.getNorthEast().toJSON(),
        southwest: placeData.geometry.viewport.getNorthEast().toJSON(),
      },
    };
    props.setLoading(true);
    addOrgGeoLocation({ variables: { input: orgGeoLocationInput } });
  };

  return (
    <div>
      <SettingsTabTitle title='Served Locations' />
      {props.locations.map((location) => (
        <ServedLocationItem key={location.id} location={location} />
      ))}

      {isAddNewLocation && (
        <div className='relative'>
          <div className='flex w-full gap-1'>
            <GooglePlacesAutocomplete onPlaceSelect={onPlaceSelect} />
            <Button
              className='text-destructive'
              variant={'ghost'}
              onClick={() => setIsAddNewLocation(false)}
            >
              <Cross1Icon />
            </Button>
          </div>
        </div>
      )}

      {!isAddNewLocation && (
        <Button
          variant='ghost'
          size='sm'
          className='-mt-3 h-fit place-self-start px-1 py-1 pr-2 hover:bg-gray-settings-button'
          onClick={() => setIsAddNewLocation(true)}
        >
          <div className='flex space-x-2'>
            <PlusIcon />
            <span>Add a new location</span>
          </div>
        </Button>
      )}
    </div>
  );
}
